import { useState } from "react";
import { Droplets, Milk, Utensils, Wheat } from "lucide-react";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { RECIPES, recipeRange } from "@/data/milletNutrition";

const ICONS = { water: Droplets, traditional: Wheat, lean: Milk, savoury: Utensils };

export const RecipeComparison = () => {
  const [serving, setServing] = useState("30");
  return (
    <section id="bowl-recipes" className="scroll-mt-6 py-12 sm:py-16" aria-labelledby="recipes-title">
      <div className="mb-7 flex flex-wrap items-end justify-between gap-5">
        <div>
          <p className="mb-2 font-mono text-xs uppercase text-terracotta">03 / Make it your own</p>
          <h2 id="recipes-title" className="font-display text-base font-semibold md:text-lg" data-testid="recipes-title">Four ways to make your bowl</h2>
          <p className="mt-2 text-sm text-muted-foreground" data-testid="recipe-portion-note">Per bowl, with {serving} g raw powder. Liquid and sweetener quantities stay the same.</p>
        </div>
        <div data-testid="recipe-serving-selector">
          <RadioGroup value={serving} onValueChange={setServing} orientation="horizontal" className="flex gap-1 rounded-lg bg-cream-muted/60 p-1" aria-label="Raw powder per recipe" data-testid="recipe-serving-list">
            {["30", "45"].map((grams) => (
              <label key={grams} htmlFor={`recipe-size-${grams}`} data-testid={`recipe-serving-label-${grams}`} className={`flex cursor-pointer items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors ${serving === grams ? "bg-cream shadow-sm" : "hover:bg-cream/60"}`}>
                <RadioGroupItem id={`recipe-size-${grams}`} value={grams} onFocus={() => setServing(grams)} aria-label={`${grams} g of dry powder`} data-testid={`recipe-serving-${grams}`} />
                {grams} g
              </label>
            ))}
          </RadioGroup>
        </div>
      </div>
      <div className="grid gap-5 md:grid-cols-2" aria-live="polite" aria-atomic="false" data-testid="recipe-comparison">
        {RECIPES.map((recipe) => {
          const Icon = ICONS[recipe.id];
          return (
            <article key={recipe.id} className="rounded-lg border border-cream-muted bg-white/70 p-5 sm:p-7" data-testid={`recipe-${recipe.id}`}>
              <div className="flex items-center gap-3">
                <Icon className="h-5 w-5 shrink-0 text-terracotta" aria-hidden="true" />
                <h3 className="text-base font-semibold" data-testid={`recipe-title-${recipe.id}`}>{recipe.title}</h3>
              </div>
              <p className="mt-2 text-xs font-medium text-ragi" data-testid={`recipe-note-${recipe.id}`}>{recipe.note}</p>
              <ul className="my-5 min-h-28 space-y-2 text-sm leading-relaxed text-muted-foreground" data-testid={`recipe-ingredients-${recipe.id}`}>
                <li>{serving} g millet powder</li>
                {recipe.ingredients.map((ingredient) => <li key={ingredient}>{ingredient}</li>)}
              </ul>
              <dl className="grid gap-3 border-t border-cream-muted pt-5 sm:grid-cols-3">
                {[["energy", "Calories"], ["carbs", "Carbs"], ["protein", "Protein"]].map(([id, label]) => (
                  <div key={id} className="flex justify-between gap-2 sm:block">
                    <dt className="text-xs text-muted-foreground">{label}</dt>
                    <dd className="text-xs font-semibold tabular-nums sm:mt-2" data-testid={`recipe-${recipe.id}-${id}`}>{recipeRange(recipe, id, Number(serving))}</dd>
                  </div>
                ))}
              </dl>
            </article>
          );
        })}
      </div>
      <p className="mt-5 text-xs leading-relaxed text-muted-foreground" data-testid="recipe-estimates-note">Approximate totals include the listed additions. Honey and buttermilk values are estimates inferred from the supplied recipes; dilution, brands and spoon sizes change nutrition. Salt and the pinch of cumin are not included in the totals. Milk and curd contain dairy.</p>
    </section>
  );
};